import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';

import { BehaviorSubject, Observable, throwError } from 'rxjs';
import { map, tap, switchMap, catchError } from 'rxjs/operators';

import { isAfter } from 'date-fns';

import { StorageService } from '../storage';
import { decodeJwt } from '../../utils/decodeJwt';
import { Usuario } from '../../models/auth/Usuario';
import { Empresa } from '../../models/auth/Empresa';
import { endpoints } from 'src/environments/endpoints';
import { AccessData } from '../../models/auth/AccessData';
import { OnboardingStatus } from '../../enum/OnboardingStatus';
import { SignInPayload } from '../../models/auth/SignInPayload';
import { SignInResponse } from '../../models/auth/SignInResponse';
import { BaseComponent } from '../../components/base/base.component';
import { RecoveryPassPayload } from '../../models/auth/RecoveryPassPayload';
import { TipoEmpresa } from '../../models/auth/TipoEmpresa';

@Injectable({
  providedIn: 'root',
})
export class AuthService extends BaseComponent {
  http = inject(HttpClient);
  storageService = inject(StorageService);

  empresaSelecionada$ = new BehaviorSubject<Empresa | null>(null);

  isAuthenticated$ = new BehaviorSubject<boolean>(false);

  accessData$ = new BehaviorSubject<AccessData | null>(null);

  onboardingStatus$ = new BehaviorSubject<OnboardingStatus | null>(null);

  usuario$ = new BehaviorSubject<Usuario | null>(null);

  empresas$ = this.usuario$.pipe(
    map((usuario) => usuario?.respostaEmpresasUsuariosDTO),
    map(
      (respostaEmpresa) =>
        respostaEmpresa?.map((e) => e.respostaEmpresaDTO) ?? []
    )
  );

  tipoUsuario$ = this.usuario$.pipe(map((user) => user?.tipo ?? null));

  constructor() {
    super();

    this.setIsAuthenticated();
    this.setUserFromLocalStorage();
    this.setSelectedEmpresaFromLocalStorage();
    this.setOnboardingStatusFromLocalStorage();
  }

  loadOnboardingStatus(): Observable<OnboardingStatus> {
    const url = endpoints.onboarding;

    return this.http.get<{ etapa: OnboardingStatus }>(url).pipe(
      map((response) => response.etapa),
      catchError(() => this.http.post<OnboardingStatus>(url, { etapa: 0 })),
      tap((etapa) => this.setOnboardingStatusToLocalStorage(etapa)),
      tap(() => this.isAuthenticated$.next(true))
    );
  }

  signIn(payload: SignInPayload): Observable<OnboardingStatus> {
    const url = endpoints.login;

    return this.http.post<SignInResponse>(url, payload).pipe(
      tap((jwtData) => this.setJwtData(jwtData)),
      switchMap(() => this.getCurrentUser()),
      switchMap(() => this.loadOnboardingStatus()),
      tap(() => this.isAuthenticated$.next(true))
    );
  }

  sendRecoveryPassEmail(email: string): Observable<void> {
    const url = endpoints.esqueceuSenha;

    return this.http.post<void>(url, { email });
  }

  recoveryPass(payload: RecoveryPassPayload): Observable<void> {
    const url = endpoints.redefinicaoSenha;

    const params = new HttpParams()
      .append('idUsuario', payload.idUsuario)
      .append('token', payload.token)
      .append('senha', payload.senha)
      .append('confirmeSenha', payload.confirmeSenha);

    return this.http.put<void>(url, {}, { params });
  }

  getCurrentUser(args?: { mudarEmpresa: boolean }): Observable<Usuario> {
    const mudarEmpresa = args?.mudarEmpresa ?? true;

    const accessData = this.accessData$.getValue();

    if (!accessData) {
      return throwError(() => new Error('Usuário não autenticado'));
    }

    return this.http.get<Usuario>(endpoints.usuarios).pipe(
      tap((user) => this.setUser(user)),
      tap((user) => {
        if (!mudarEmpresa) {
          const empresaSelecionada = user.respostaEmpresasUsuariosDTO.find(
            (e) =>
              e.respostaEmpresaDTO.guid ===
              this.empresaSelecionada$.getValue()?.guid
          );

          if (empresaSelecionada) {
            this.setSelectedEmpresa(empresaSelecionada.respostaEmpresaDTO);
            return;
          }
          // A empresa guardada nao esta mais na lista do usuario: cai na escolha padrao abaixo.
        }
        const primeiraFilial = user.respostaEmpresasUsuariosDTO.find(
          (e) => e.respostaEmpresaDTO.tipoEmpresa === TipoEmpresa.Matriz
        )?.respostaEmpresaDTO;

        if (!primeiraFilial) {
          const primeiraEmpresa =
            user.respostaEmpresasUsuariosDTO[0].respostaEmpresaDTO;

          this.setSelectedEmpresa(primeiraEmpresa);
          return;
        }

        this.setSelectedEmpresa(primeiraFilial);
      })
    );
  }

  signOut(): Observable<void> {
    return this.http
      .post<void>(endpoints.logout, {})
      .pipe(tap(() => this.removeAuthenticationData()));
  }

  setSelectedEmpresa(empresa: Empresa): void {
    this.empresaSelecionada$.next(empresa);

    this.storageService.setItem('empresa', empresa);
  }

  setOnboardingStatusToLocalStorage(onboardingStatus: OnboardingStatus): void {
    this.onboardingStatus$.next(onboardingStatus);

    this.storageService.setItem('onboardingStatus', onboardingStatus);
  }

  getToken(): string | null {
    const jwtData = this.storageService.getItem<SignInResponse>('JWT_DATA');

    if (!jwtData) {
      return null;
    }

    return jwtData.jsonWebToken;
  }

  removeAuthenticationData(): void {
    this.removeUserToLocalStorage();

    this.isAuthenticated$.next(false);
  }

  private setOnboardingStatusFromLocalStorage(): void {
    const onboardingStatus =
      this.storageService.getItem<OnboardingStatus>('onboardingStatus');

    this.onboardingStatus$.next(onboardingStatus);
  }

  private setSelectedEmpresaFromLocalStorage(): void {
    const empresa = this.storageService.getItem<Empresa>('empresa');

    this.empresaSelecionada$.next(empresa);
  }

  private setUserFromLocalStorage(): void {
    const user = this.storageService.getItem<Usuario>('user');

    this.usuario$.next(user);
  }

  private removeUserToLocalStorage(): void {
    this.storageService.removeItem('JWT_DATA');
    this.isAuthenticated$.next(false);
  }

  private setJwtData(jwtData: SignInResponse): void {
    this.storageService.setItem('JWT_DATA', jwtData);

    const decodedJwt = decodeJwt<AccessData>(jwtData.jsonWebToken);

    if (decodedJwt) {
      this.accessData$.next(decodedJwt);
    }
  }

  private setIsAuthenticated(): void {
    const jwtData = this.storageService.getItem<SignInResponse>('JWT_DATA');

    this.isAuthenticated$.next(!!jwtData);

    if (!jwtData) {
      return;
    }

    const decodedJwt = decodeJwt<AccessData>(jwtData.jsonWebToken);

    if (!decodedJwt) {
      return;
    }

    const expiresWhen = new Date(decodedJwt.exp * 1000);

    if (isAfter(new Date(), expiresWhen)) {
      this.removeAuthenticationData();
      this.router.navigate(['autenticacao', 'entrar']);
    }

    this.accessData$.next(decodedJwt);
  }

  private setUser(user: Usuario): void {
    this.usuario$.next(user);

    this.storageService.setItem('user', user);
  }
}
